const LIGHTBOX_PREVIEW_VERSION = "1.2.15";
const CM_PER_INCH = 2.54;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALPHA_THRESHOLD = 10;
const DEFAULT_TOTAL_THICKNESS_CM = 3.2;
const DEFAULT_GLOW_THICKNESS_CM = 1.0;
const SHELL_WALL_THICKNESS = 0.2;
const PANEL_FIT_GAP = 0.05;
const FRONT_LIP_OVERLAP = 0.2;
const FRONT_LIP_DEPTH = 0.2;
const FRONT_PANEL_DEPTH = 0.16;
const BACK_PANEL_DEPTH = 0.18;
const LIGHT_ON_BACKGROUND = "#2D303F";
const LIGHT_OFF_WALL = "#d3d8da";
const LIGHT_OFF_BACKGROUND = LIGHT_OFF_WALL;
const SHELL_BLACK_COLOR = "#070a0d";
const THREE_MODULE_CDN_URL = "./three.module.js?v=0.183.2";
const ORBIT_CONTROLS_CDN_URL = "./OrbitControls.js?v=0.183.2";
const DEFAULT_LOGO_URL = "./assets/Demo_logo.png";
const DEFAULT_LOGO_FALLBACK_URL = "https://cdn.shopify.com/s/files/1/0688/1394/6927/files/Demo_logo.png?v=1782207898";
const DEFAULT_PREVIEW_DIMENSIONS = { widthCm: 30, heightCm: 28.4 };
const DISPLAY_THICKNESS_CM = 3.5;
const SVG_RASTER_LONG_SIDE = 2400;
const MIN_RENDER_DIMENSION_CM = 0.001;
const WALL_GLOW_SCALE_X = 2.65;
const WALL_GLOW_SCALE_Y = 2.35;
const WALL_GLOW_EDGE_PADDING_RATIO = 0.24;
const WALL_SHADOW_SCALE_X = 1.04;
const WALL_SHADOW_SCALE_Y = 1.04;
const WALL_SHADOW_EDGE_PADDING_RATIO = 0.015;

const CONFIG = {
  quoteUrl: "/pages/contact",
  quoteSubmitUrl: "",
  threeModuleUrl: THREE_MODULE_CDN_URL,
  orbitControlsUrl: ORBIT_CONTROLS_CDN_URL,
  googleAdsEventName: "",
  googleAdsSendTo: "",
  brandName: "myneonup.com",
  exportSize: { width: 1600, height: 2000 },
  quotePreviewSize: { width: 900, height: 1125 },
  quotePreviewType: "image/jpeg",
  quotePreviewQuality: 0.86,
  defaultLogoUrl: DEFAULT_LOGO_URL,
  defaultLogoFallbackUrl: DEFAULT_LOGO_FALLBACK_URL,
  ...(window.LightboxPreviewConfig || {})
};

let THREE = null;
let OrbitControlsClass = null;

window.LightboxPreviewVersion = LIGHTBOX_PREVIEW_VERSION;

const SIDE_GLOW_PRESETS = [
  { name: "White", slug: "white", color: "#ffffff" },
  { name: "Red", slug: "red", color: "#ff3b30" },
  { name: "Green", slug: "green", color: "#40d461" },
  { name: "Blue", slug: "blue", color: "#3f8cff" },
  { name: "Yellow", slug: "yellow", color: "#ffd21f" },
  { name: "Orange", slug: "orange", color: "#F76400" },
  { name: "Purple", slug: "purple", color: "#7005CE" },
  { name: "Pink", slug: "pink", color: "#ff4fd8" },
  { name: "Black", slug: "black", color: SHELL_BLACK_COLOR, opaque: true }
];

const STANDARD_SIZE_OPTIONS = {
  20: '20cm / 8"',
  30: '30cm / 12"',
  40: '40cm / 16"',
  50: '50cm / 20"',
  60: '60cm / 24"'
};

const elements = {
  upload: document.getElementById("logo-upload"),
  dropzone: document.getElementById("upload-dropzone"),
  fileStatus: document.getElementById("file-status"),
  sizeOptions: document.getElementById("size-options"),
  customPanel: document.getElementById("custom-size-panel"),
  customInput: document.getElementById("custom-size-input"),
  sizeValidation: document.getElementById("size-validation"),
  sizeReadout: document.getElementById("size-readout"),
  unitToggle: document.querySelector(".lbp-unit-toggle"),
  colorGrid: document.getElementById("side-glow-options"),
  mountingToggle: document.getElementById("mounting-toggle"),
  lightingToggle: document.getElementById("lighting-toggle"),
  quoteForm: document.getElementById("quote-form"),
  quoteName: document.getElementById("quote-name"),
  quoteEmail: document.getElementById("quote-email"),
  quoteMessage: document.getElementById("quote-message"),
  quoteCompany: document.getElementById("quote-company"),
  quoteSubmit: document.getElementById("quote-submit"),
  quoteStatus: document.getElementById("quote-status"),
  viewport: document.getElementById("lightbox-viewport"),
  viewportMessage: document.getElementById("viewport-message"),
  renderStatus: document.getElementById("render-status"),
  previewTitle: document.getElementById("preview-title"),
  selectedSizeMeta: document.getElementById("selected-size-meta"),
  colorMeta: document.getElementById("color-meta")
};

const state = {
  logoAsset: null,
  originalLogoFile: null,
  sizeMode: "standard",
  standardSizeCm: 30,
  customUnit: "cm",
  customValue: 30,
  longestSideCm: 30,
  dimensions: { ...DEFAULT_PREVIEW_DIMENSIONS },
  sideGlow: SIDE_GLOW_PRESETS[0],
  mounting: "wall",
  lighting: "on",
  sourceWarning: ""
};

let scene = null;

async function loadThreeRuntime() {
  if (THREE && OrbitControlsClass) {
    return;
  }

  const [threeModule, orbitControlsModule] = await Promise.all([
    import(normalizeModuleUrl(CONFIG.threeModuleUrl, THREE_MODULE_CDN_URL)),
    import(normalizeModuleUrl(CONFIG.orbitControlsUrl, ORBIT_CONTROLS_CDN_URL))
  ]);

  if (!threeModule?.Scene || !orbitControlsModule?.OrbitControls) {
    throw new Error(
      "The 3D engine assets could not be loaded. In Shopify, upload three.module.js, three.core.js, and OrbitControls.js to Assets and keep their URLs in LightboxPreviewConfig."
    );
  }

  THREE = threeModule;
  OrbitControlsClass = orbitControlsModule.OrbitControls;
}

function normalizeModuleUrl(value, fallback) {
  const source = String(value || "").trim();
  if (!source) {
    return fallback;
  }
  if (source.startsWith("//")) {
    return `${window.location.protocol}${source}`;
  }
  return source;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clamp01(value) {
  return clamp(value, 0, 1);
}

function lerp(start, end, amount) {
  return start + (end - start) * amount;
}

function wrapHue(hue) {
  return ((hue % 1) + 1) % 1;
}

function lerpHue(start, end, amount) {
  const delta = ((end - start + 1.5) % 1) - 0.5;
  return wrapHue(start + delta * amount);
}

function isHueBetween(hue, startDegrees, endDegrees) {
  const start = startDegrees / 360;
  const end = endDegrees / 360;
  const safeHue = wrapHue(hue);
  return start <= end ? safeHue >= start && safeHue <= end : safeHue >= start || safeHue <= end;
}

function rgbToHsv(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;

  if (delta > 1e-6) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6;
    } else {
      h = ((r - g) / delta + 4) / 6;
    }
  }

  return { h, s: max <= 1e-6 ? 0 : delta / max, v: max };
}

function hsvToRgb(h, s, v) {
  const safeHue = wrapHue(h) * 6;
  const sector = Math.floor(safeHue);
  const fraction = safeHue - sector;
  const p = v * (1 - s);
  const q = v * (1 - fraction * s);
  const t = v * (1 - (1 - fraction) * s);

  switch (sector % 6) {
    case 0:
      return { r: v, g: t, b: p };
    case 1:
      return { r: q, g: v, b: p };
    case 2:
      return { r: p, g: v, b: t };
    case 3:
      return { r: p, g: q, b: v };
    case 4:
      return { r: t, g: p, b: v };
    default:
      return { r: v, g: p, b: q };
  }
}

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (/^https?:\/\//i.test(src)) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The image could not be loaded. Please try another common image format."));
    image.src = src;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("The file could not be read. Please choose the file again."));
    reader.readAsDataURL(file);
  });
}

function isSvgSource(source, dataUrl, name) {
  const sourceText = typeof source === "string" ? source : "";
  return (
    /^data:image\/svg\+xml/i.test(dataUrl) ||
    /\.svg(?:$|\?)/i.test(sourceText) ||
    /\.svg$/i.test(name || "") ||
    (typeof source !== "string" && source?.type === "image/svg+xml")
  );
}

function decodeSvgDataUrl(dataUrl) {
  if (!/^data:image\/svg\+xml/i.test(dataUrl)) {
    return "";
  }

  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex < 0) {
    return "";
  }

  const meta = dataUrl.slice(0, commaIndex);
  const body = dataUrl.slice(commaIndex + 1);
  try {
    return /;base64/i.test(meta) ? atob(body) : decodeURIComponent(body);
  } catch {
    return "";
  }
}

function parseSvgLength(value) {
  if (!value || /%$/.test(value)) {
    return null;
  }
  const match = String(value).trim().match(/^([0-9]*\.?[0-9]+)/);
  return match ? Number(match[1]) : null;
}

function getSvgAspectRatio(svgText, fallbackAspect) {
  if (!svgText) {
    return fallbackAspect;
  }

  const viewBoxMatch = svgText.match(/\bviewBox\s*=\s*["']([^"']+)["']/i);
  if (viewBoxMatch) {
    const values = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);
    if (values.length >= 4 && values[2] > 0 && values[3] > 0) {
      return values[2] / values[3];
    }
  }

  const widthMatch = svgText.match(/\bwidth\s*=\s*["']([^"']+)["']/i);
  const heightMatch = svgText.match(/\bheight\s*=\s*["']([^"']+)["']/i);
  const width = parseSvgLength(widthMatch?.[1]);
  const height = parseSvgLength(heightMatch?.[1]);
  return width && height ? width / height : fallbackAspect;
}

function rasterizeSvgImage(image, dataUrl) {
  const fallbackWidth = image.naturalWidth || image.width || 800;
  const fallbackHeight = image.naturalHeight || image.height || 400;
  const aspect = Math.max(0.05, getSvgAspectRatio(decodeSvgDataUrl(dataUrl), fallbackWidth / Math.max(fallbackHeight, 1)));
  const width = aspect >= 1 ? SVG_RASTER_LONG_SIDE : Math.round(SVG_RASTER_LONG_SIDE * aspect);
  const height = aspect >= 1 ? Math.round(SVG_RASTER_LONG_SIDE / aspect) : SVG_RASTER_LONG_SIDE;
  const canvas = createCanvas(width, height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  return canvas;
}

function buildFrontTextureCanvas(cropCanvas) {
  const canvas = createCanvas(cropCanvas.width, cropCanvas.height);
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(cropCanvas, 0, 0);
  return canvas;
}

function buildLogoEmissionTextureCanvas(frontTextureCanvas) {
  const canvas = createCanvas(frontTextureCanvas.width, frontTextureCanvas.height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(frontTextureCanvas, 0, 0);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  const coolWhiteHue = 205 / 360;

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3] / 255;
    if (alpha <= 0.001) {
      continue;
    }

    const sourceR = data[index] / 255;
    const sourceG = data[index + 1] / 255;
    const sourceB = data[index + 2] / 255;
    const hsv = rgbToHsv(sourceR, sourceG, sourceB);
    const luminance = sourceR * 0.2126 + sourceG * 0.7152 + sourceB * 0.0722;
    const maxChannel = Math.max(sourceR, sourceG, sourceB);
    const isLightNeutral = hsv.s < 0.08 && luminance > 0.72;
    const isInkMask = hsv.v < 0.035 || (hsv.v < 0.075 && hsv.s < 0.3);
    const isShadowColor =
      luminance < 0.42 || maxChannel < 0.44 || (hsv.s < 0.16 && luminance < 0.58);

    if (isInkMask) {
      data[index] = Math.round(sourceR * 18);
      data[index + 1] = Math.round(sourceG * 18);
      data[index + 2] = Math.round(sourceB * 18);
      continue;
    }

    let outputColor = { r: sourceR, g: sourceG, b: sourceB };

    if (isShadowColor) {
      const shadowStrength = clamp(0.12 + luminance * 0.55 + hsv.s * 0.08, 0.06, 0.38);
      outputColor = {
        r: clamp01(sourceR * shadowStrength),
        g: clamp01(sourceG * shadowStrength),
        b: clamp01(sourceB * shadowStrength)
      };
    } else if (isLightNeutral) {
      const grayLift = 1 - hsv.v;
      const neutralLift = hsvToRgb(
        coolWhiteHue,
        clamp01(0.02 + grayLift * 0.04),
        clamp01(Math.max(hsv.v, 0.82) + grayLift * 0.02)
      );
      outputColor = {
        r: clamp01(lerp(neutralLift.r, 0.94, 0.04)),
        g: clamp01(lerp(neutralLift.g, 0.98, 0.04)),
        b: clamp01(lerp(neutralLift.b, 1, 0.04))
      };
    } else {
      const targetMax = clamp(Math.max(maxChannel, maxChannel * 1.08 + 0.04), maxChannel, 0.92);
      const uniformScale = maxChannel > 0.001 ? targetMax / maxChannel : 1;
      const highlightLift = luminance > 0.65 ? 0.015 : 0;
      outputColor = {
        r: clamp01(sourceR * uniformScale + highlightLift * (1 - sourceR)),
        g: clamp01(sourceG * uniformScale + highlightLift * (1 - sourceG)),
        b: clamp01(sourceB * uniformScale + highlightLift * (1 - sourceB))
      };
    }

    data[index] = Math.round(outputColor.r * 255);
    data[index + 1] = Math.round(outputColor.g * 255);
    data[index + 2] = Math.round(outputColor.b * 255);
  }

  context.putImageData(imageData, 0, 0);
  return canvas;
}

function analyzeOpaqueBounds(image, alphaThreshold) {
  const sourceWidth = image.naturalWidth || image.width || 800;
  const sourceHeight = image.naturalHeight || image.height || 400;
  const canvas = createCanvas(sourceWidth, sourceHeight);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let hasOpaquePixel = false;
  let hasTransparentPixel = false;

  for (let index = 3; index < data.length; index += 4) {
    const alpha = data[index];
    if (alpha > alphaThreshold) {
      hasOpaquePixel = true;
      const pixelIndex = (index - 3) / 4;
      const x = pixelIndex % width;
      const y = Math.floor(pixelIndex / width);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }

    if (alpha < 250) {
      hasTransparentPixel = true;
    }
  }

  if (!hasOpaquePixel) {
    throw new Error("No visible logo pixels were detected. Please upload a non-empty image.");
  }

  if (!hasTransparentPixel) {
    const cropCanvas = createCanvas(width, height);
    cropCanvas.getContext("2d").drawImage(canvas, 0, 0);
    return {
      bbox: { x: 0, y: 0, width, height },
      cropCanvas,
      outlineMode: "full-image"
    };
  }

  const bbox = {
    x: Math.max(0, minX),
    y: Math.max(0, minY),
    width: Math.max(1, maxX - minX + 1),
    height: Math.max(1, maxY - minY + 1)
  };
  const cropCanvas = createCanvas(bbox.width, bbox.height);
  cropCanvas
    .getContext("2d")
    .drawImage(canvas, bbox.x, bbox.y, bbox.width, bbox.height, 0, 0, bbox.width, bbox.height);

  return { bbox, cropCanvas, outlineMode: "alpha-mask" };
}

function buildMask(canvas, alphaThreshold, targetResolution) {
  const longestSide = Math.max(canvas.width, canvas.height);
  const traceScale = Math.min(2.5, Math.max(0.6, targetResolution / longestSide));
  const workWidth = Math.max(6, Math.round(canvas.width * traceScale));
  const workHeight = Math.max(6, Math.round(canvas.height * traceScale));
  const workCanvas = createCanvas(workWidth, workHeight);
  const workContext = workCanvas.getContext("2d", { willReadFrequently: true });
  workContext.imageSmoothingEnabled = true;
  workContext.drawImage(canvas, 0, 0, workWidth, workHeight);

  const { data } = workContext.getImageData(0, 0, workWidth, workHeight);
  const mask = Array.from({ length: workHeight + 2 }, () =>
    Array.from({ length: workWidth + 2 }, () => false)
  );

  for (let y = 0; y < workHeight; y += 1) {
    for (let x = 0; x < workWidth; x += 1) {
      mask[y + 1][x + 1] = data[(y * workWidth + x) * 4 + 3] > alphaThreshold;
    }
  }

  return { mask, workWidth, workHeight };
}

function addEdge(edges, startX, startY, endX, endY) {
  edges.push({
    start: { x: startX, y: startY },
    end: { x: endX, y: endY },
    visited: false
  });
}

function buildBoundaryEdges(mask, workWidth, workHeight) {
  const edges = [];

  for (let y = 1; y <= workHeight; y += 1) {
    for (let x = 1; x <= workWidth; x += 1) {
      if (!mask[y][x]) {
        continue;
      }

      if (!mask[y - 1][x]) addEdge(edges, x, y, x + 1, y);
      if (!mask[y][x + 1]) addEdge(edges, x + 1, y, x + 1, y + 1);
      if (!mask[y + 1][x]) addEdge(edges, x + 1, y + 1, x, y + 1);
      if (!mask[y][x - 1]) addEdge(edges, x, y + 1, x, y);
    }
  }

  return edges;
}

function pointKey(point) {
  return `${point.x},${point.y}`;
}

function pointsEqual(left, right) {
  return left.x === right.x && left.y === right.y;
}

function edgeDirection(edge) {
  const dx = edge.end.x - edge.start.x;
  const dy = edge.end.y - edge.start.y;
  if (dx > 0) return 0;
  if (dy > 0) return 1;
  if (dx < 0) return 2;
  return 3;
}

function chooseNextEdge(currentEdge, candidates) {
  if (candidates.length === 1) {
    return candidates[0];
  }

  const direction = edgeDirection(currentEdge);
  const turnPriority = { 1: 0, 0: 1, 3: 2, 2: 3 };

  return candidates
    .map((candidate) => {
      const nextDirection = edgeDirection(candidate.edge);
      const turn = (nextDirection - direction + 4) % 4;
      return { ...candidate, rank: turnPriority[turn] ?? 99 };
    })
    .sort((left, right) => left.rank - right.rank)[0];
}

function buildLoops(edges) {
  const outgoing = new Map();

  edges.forEach((edge, index) => {
    const key = pointKey(edge.start);
    if (!outgoing.has(key)) {
      outgoing.set(key, []);
    }
    outgoing.get(key).push(index);
  });

  const loops = [];

  for (let index = 0; index < edges.length; index += 1) {
    if (edges[index].visited) {
      continue;
    }

    const loop = [];
    let currentIndex = index;
    let guard = 0;

    while (guard < edges.length * 2) {
      const edge = edges[currentIndex];
      if (edge.visited) {
        break;
      }

      edge.visited = true;
      if (loop.length === 0) {
        loop.push({ ...edge.start });
      }
      loop.push({ ...edge.end });

      if (pointsEqual(edge.end, loop[0])) {
        break;
      }

      const nextCandidates = (outgoing.get(pointKey(edge.end)) || [])
        .filter((candidateIndex) => !edges[candidateIndex].visited)
        .map((candidateIndex) => ({ index: candidateIndex, edge: edges[candidateIndex] }));

      if (!nextCandidates.length) {
        break;
      }

      currentIndex = chooseNextEdge(edge, nextCandidates).index;
      guard += 1;
    }

    if (loop.length >= 4 && pointsEqual(loop[0], loop[loop.length - 1])) {
      loop.pop();
    }

    if (loop.length >= 3) {
      loops.push(loop);
    }
  }

  return loops;
}

function polygonArea(points) {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

function pointInPolygon(point, polygon) {
  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    const intersects =
      current.y > point.y !== previous.y > point.y &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y || 1e-6) + current.x;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

function areCollinear(previous, current, next) {
  const cross =
    (current.x - previous.x) * (next.y - current.y) -
    (current.y - previous.y) * (next.x - current.x);
  return Math.abs(cross) < 1e-6;
}

function stripCollinear(points) {
  if (points.length <= 3) {
    return points;
  }

  const cleaned = [];
  for (let index = 0; index < points.length; index += 1) {
    const previous = points[(index - 1 + points.length) % points.length];
    const current = points[index];
    const next = points[(index + 1) % points.length];
    if (!areCollinear(previous, current, next)) {
      cleaned.push(current);
    }
  }

  return cleaned.length >= 3 ? cleaned : points;
}

function simplifyLoop(points, tolerance) {
  if (points.length <= 12) {
    return stripCollinear(points);
  }

  const simplified = [points[0]];
  for (let index = 1; index < points.length; index += 1) {
    const current = points[index];
    const last = simplified[simplified.length - 1];
    if (Math.hypot(current.x - last.x, current.y - last.y) >= tolerance) {
      simplified.push(current);
    }
  }

  if (
    simplified.length > 1 &&
    Math.hypot(simplified[0].x - simplified[simplified.length - 1].x, simplified[0].y - simplified[simplified.length - 1].y) <
      tolerance
  ) {
    simplified.pop();
  }

  return stripCollinear(simplified.length >= 3 ? simplified : points);
}

function smoothLoop(points, iterations = 1) {
  let output = points;

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    if (output.length < 4) {
      break;
    }

    const next = [];
    for (let index = 0; index < output.length; index += 1) {
      const current = output[index];
      const following = output[(index + 1) % output.length];
      next.push({
        x: current.x * 0.75 + following.x * 0.25,
        y: current.y * 0.75 + following.y * 0.25
      });
      next.push({
        x: current.x * 0.25 + following.x * 0.75,
        y: current.y * 0.25 + following.y * 0.75
      });
    }
    output = next;
  }

  return output;
}

function normalizeLoop(points, workWidth, workHeight) {
  return points.map((point) => ({
    x: (point.x - 1) / workWidth - 0.5,
    y: 0.5 - (point.y - 1) / workHeight
  }));
}

function ensureCounterClockwise(points) {
  return polygonArea(points) < 0 ? [...points].reverse() : points;
}

function ensureClockwise(points) {
  return polygonArea(points) > 0 ? [...points].reverse() : points;
}

function traceNormalizedLoops(cropCanvas, alphaThreshold, targetResolution) {
  const { mask, workWidth, workHeight } = buildMask(cropCanvas, alphaThreshold, targetResolution);
  const edges = buildBoundaryEdges(mask, workWidth, workHeight);
  const loops = buildLoops(edges);

  return loops
    .map((loop) => simplifyLoop(loop, 0.65))
    .map((loop) => smoothLoop(loop, 2))
    .map((loop) => normalizeLoop(loop, workWidth, workHeight))
    .filter((loop) => loop.length >= 3)
    .filter((loop) => Math.abs(polygonArea(loop)) > 0.0005);
}

function createFullImageRectangleLoop() {
  return [
    { x: -0.5, y: 0.5 },
    { x: 0.5, y: 0.5 },
    { x: 0.5, y: -0.5 },
    { x: -0.5, y: -0.5 }
  ];
}

function createInsetRectangleLoop({ widthCm, heightCm, insetCm }) {
  const insetX = clamp(insetCm / Math.max(widthCm, 0.001), 0, 0.48);
  const insetY = clamp(insetCm / Math.max(heightCm, 0.001), 0, 0.48);
  const x = 0.5 - insetX;
  const y = 0.5 - insetY;
  return [
    { x: -x, y },
    { x, y },
    { x, y: -y },
    { x: -x, y: -y }
  ];
}

function insetLogoLoops(normalizedLoops, { widthCm, heightCm, insetCm, outlineMode }) {
  if (outlineMode === "full-image") {
    return [createInsetRectangleLoop({ widthCm, heightCm, insetCm })];
  }
  return insetNormalizedLoops(normalizedLoops, { widthCm, heightCm, insetCm });
}

function extractSolidNormalizedLoops(normalizedLoops) {
  const loops = normalizedLoops
    .map((loop, index) => ({
      index,
      points: ensureCounterClockwise(loop),
      absArea: Math.abs(polygonArea(loop))
    }))
    .filter((loop) => loop.points.length >= 3 && loop.absArea > 0.0005);

  const byArea = [...loops].sort((left, right) => right.absArea - left.absArea);

  return byArea
    .filter(
      (loop) =>
        !byArea.some(
          (candidate) =>
            candidate.index !== loop.index &&
            candidate.absArea > loop.absArea &&
            pointInPolygon(loop.points[0], candidate.points)
        )
    )
    .sort((left, right) => left.index - right.index)
    .map((loop) => loop.points);
}

function drawNormalizedLoopsPath(context, normalizedLoops, width, height) {
  context.beginPath();

  normalizedLoops.forEach((loop) => {
    loop.forEach((point, index) => {
      const x = (point.x + 0.5) * width;
      const y = (0.5 - point.y) * height;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    });
    context.closePath();
  });
}

function insetNormalizedLoops(normalizedLoops, { widthCm, heightCm, insetCm, targetResolution = 1200 } = {}) {
  if (!normalizedLoops?.length || insetCm <= 0) {
    return normalizedLoops || [];
  }

  const longestSideCm = Math.max(widthCm, heightCm, 0.001);
  const pixelsPerCm = targetResolution / longestSideCm;
  const canvasWidth = Math.max(64, Math.round(widthCm * pixelsPerCm));
  const canvasHeight = Math.max(64, Math.round(heightCm * pixelsPerCm));
  const insetPixels = insetCm * pixelsPerCm;
  const canvas = createCanvas(canvasWidth, canvasHeight);
  const context = canvas.getContext("2d", { willReadFrequently: true });

  context.fillStyle = "#ffffff";
  drawNormalizedLoopsPath(context, normalizedLoops, canvasWidth, canvasHeight);
  context.fill("evenodd");

  context.save();
  context.globalCompositeOperation = "destination-out";
  context.strokeStyle = "#000000";
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = insetPixels * 2;
  drawNormalizedLoopsPath(context, normalizedLoops, canvasWidth, canvasHeight);
  context.stroke();
  context.restore();

  return traceNormalizedLoops(canvas, ALPHA_THRESHOLD, Math.max(canvasWidth, canvasHeight));
}

async function buildLogoAssetFromSource(source, options = {}) {
  const dataUrl = typeof source === "string" ? source : await readFileAsDataUrl(source);
  const name = typeof source === "string" ? options.name || "demo-logo.png" : source.name;
  const image = await loadImage(dataUrl);
  const isSvg = isSvgSource(source, dataUrl, name);
  const rasterSource = isSvg ? rasterizeSvgImage(image, dataUrl) : image;
  const { bbox, cropCanvas, outlineMode } = analyzeOpaqueBounds(rasterSource, ALPHA_THRESHOLD);
  const frontTextureCanvas = buildFrontTextureCanvas(cropCanvas);
  const emissiveTextureCanvas = buildLogoEmissionTextureCanvas(frontTextureCanvas);
  const normalizedLoops =
    outlineMode === "full-image"
      ? [createFullImageRectangleLoop()]
      : traceNormalizedLoops(cropCanvas, ALPHA_THRESHOLD, isSvg ? 1200 : 720);

  if (!normalizedLoops.length) {
    throw new Error("The logo outline could not be extracted. Please try a clearer logo image.");
  }

  return {
    name,
    dataUrl,
    previewUrl: cropCanvas.toDataURL("image/png"),
    textureCanvas: cropCanvas,
    frontTextureCanvas,
    emissiveTextureCanvas,
    sourceSize: { width: rasterSource.naturalWidth || rasterSource.width, height: rasterSource.naturalHeight || rasterSource.height },
    bbox,
    aspect: bbox.width / Math.max(bbox.height, 1),
    outlineMode,
    sourceType: isSvg ? "svg" : "raster",
    normalizedLoops,
    solidNormalizedLoops: extractSolidNormalizedLoops(normalizedLoops)
  };
}

function buildShapes(normalizedLoops, widthCm, heightCm) {
  const loops = normalizedLoops
    .map((loop, index) => ({
      index,
      points: loop.map((point) => new THREE.Vector2(point.x * widthCm, point.y * heightCm))
    }))
    .filter((loop) => Math.abs(polygonArea(loop.points)) > 0.2)
    .map((loop) => ({ ...loop, absArea: Math.abs(polygonArea(loop.points)) }));

  const byArea = [...loops].sort((left, right) => right.absArea - left.absArea);
  const metadata = new Map();

  byArea.forEach((loop) => {
    const containers = byArea.filter(
      (candidate) =>
        candidate.index !== loop.index &&
        candidate.absArea > loop.absArea &&
        pointInPolygon(loop.points[0], candidate.points)
    );
    metadata.set(loop.index, { ...loop, depth: containers.length });
  });

  const shapes = new Map();

  byArea.forEach((loop) => {
    const data = metadata.get(loop.index);
    if (data.depth % 2 === 0) {
      shapes.set(loop.index, new THREE.Shape(ensureCounterClockwise(loop.points)));
      return;
    }

    const parents = byArea
      .filter(
        (candidate) =>
          candidate.index !== loop.index &&
          candidate.absArea > loop.absArea &&
          pointInPolygon(loop.points[0], candidate.points) &&
          metadata.get(candidate.index).depth % 2 === 0
      )
      .sort((left, right) => left.absArea - right.absArea);

    const parentShape = parents.length ? shapes.get(parents[0].index) : null;
    if (parentShape) {
      parentShape.holes.push(new THREE.Path(ensureClockwise(loop.points)));
    }
  });

  return [...shapes.values()];
}

function cloneShapeContour(shape, segments = 72) {
  return new THREE.Path(shape.getPoints(segments));
}

function getShapeContourPoints(shape, segments = 160) {
  return shape.getPoints(segments);
}

function clonePathContour(path, segments = 72) {
  return new THREE.Path(path.getPoints(segments));
}

function getPathContourPoints(path, segments = 72) {
  return path.getPoints(segments);
}

function pointInAnyShapeHole(point, shape) {
  return shape.holes.some((hole) => pointInPolygon(point, getPathContourPoints(hole, 64)));
}

function buildRingShapes(outerShapes, innerShapes) {
  const ringShapes = [];

  outerShapes.forEach((outerShape) => {
    const outerContour = getShapeContourPoints(outerShape, 160);
    const ringShape = new THREE.Shape(ensureCounterClockwise(outerContour));

    innerShapes.forEach((innerShape) => {
      const innerContour = getShapeContourPoints(innerShape, 64);
      const samplePoint = innerContour[0];
      if (samplePoint && pointInPolygon(samplePoint, outerContour) && !pointInAnyShapeHole(samplePoint, outerShape)) {
        ringShape.holes.push(cloneShapeContour(innerShape));

        innerShape.holes.forEach((innerHole) => {
          const innerHoleContour = getPathContourPoints(innerHole, 64);
          const innerHoleSample = innerHoleContour[0];
          if (!innerHoleSample || !pointInPolygon(innerHoleSample, outerContour)) {
            return;
          }

          const matchingOuterHole = outerShape.holes.find((outerHole) => {
            const outerHoleContour = getPathContourPoints(outerHole, 64);
            return outerHoleContour[0] && pointInPolygon(outerHoleContour[0], innerHoleContour);
          });

          if (!matchingOuterHole) {
            return;
          }

          const holeRing = new THREE.Shape(ensureCounterClockwise(innerHoleContour));
          holeRing.holes.push(clonePathContour(matchingOuterHole));
          ringShapes.push(holeRing);
        });
      }
    });

    ringShapes.push(ringShape);
  });

  return ringShapes;
}

function createNormalizedUvGenerator(widthCm, heightCm) {
  const safeWidth = Math.max(widthCm, 0.001);
  const safeHeight = Math.max(heightCm, 0.001);
  const halfWidth = safeWidth / 2;
  const halfHeight = safeHeight / 2;

  return {
    generateTopUV(_geometry, vertices, indexA, indexB, indexC) {
      return [indexA, indexB, indexC].map((index) => {
        const x = vertices[index * 3];
        const y = vertices[index * 3 + 1];
        return new THREE.Vector2(
          THREE.MathUtils.clamp((x + halfWidth) / safeWidth, 0, 1),
          THREE.MathUtils.clamp((y + halfHeight) / safeHeight, 0, 1)
        );
      });
    },
    generateSideWallUV(_geometry, vertices, indexA, indexB, indexC, indexD) {
      const ax = vertices[indexA * 3];
      const ay = vertices[indexA * 3 + 1];
      const az = vertices[indexA * 3 + 2];
      const bx = vertices[indexB * 3];
      const by = vertices[indexB * 3 + 1];
      const bz = vertices[indexB * 3 + 2];
      const cx = vertices[indexC * 3];
      const cy = vertices[indexC * 3 + 1];
      const cz = vertices[indexC * 3 + 2];
      const dx = vertices[indexD * 3];
      const dy = vertices[indexD * 3 + 1];
      const dz = vertices[indexD * 3 + 2];

      if (Math.abs(ay - by) < Math.abs(ax - bx)) {
        return [
          new THREE.Vector2(ax / safeWidth, 1 - az),
          new THREE.Vector2(bx / safeWidth, 1 - bz),
          new THREE.Vector2(cx / safeWidth, 1 - cz),
          new THREE.Vector2(dx / safeWidth, 1 - dz)
        ];
      }

      return [
        new THREE.Vector2(ay / safeHeight, 1 - az),
        new THREE.Vector2(by / safeHeight, 1 - bz),
        new THREE.Vector2(cy / safeHeight, 1 - cz),
        new THREE.Vector2(dy / safeHeight, 1 - dz)
      ];
    }
  };
}

function createTextureFromCanvas(canvas) {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createRadialCanvasTexture({
  color = "#ffffff",
  innerAlpha = 0.5,
  outerAlpha = 0,
  width = 1024,
  height = 1024,
  darkCenter = false
} = {}) {
  const canvas = createCanvas(width, height);
  const context = canvas.getContext("2d");
  const gradient = context.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, width * 0.5);
  const rgb = new THREE.Color(color);
  const center = darkCenter
    ? `rgba(0, 0, 0, ${innerAlpha})`
    : `rgba(${Math.round(rgb.r * 255)}, ${Math.round(rgb.g * 255)}, ${Math.round(rgb.b * 255)}, ${innerAlpha})`;
  const edge = darkCenter
    ? `rgba(0, 0, 0, ${outerAlpha})`
    : `rgba(${Math.round(rgb.r * 255)}, ${Math.round(rgb.g * 255)}, ${Math.round(rgb.b * 255)}, ${outerAlpha})`;
  gradient.addColorStop(0, center);
  gradient.addColorStop(0.58, darkCenter ? `rgba(0, 0, 0, ${innerAlpha * 0.28})` : center.replace(String(innerAlpha), String(innerAlpha * 0.35)));
  gradient.addColorStop(1, edge);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
  return createTextureFromCanvas(canvas);
}

function createTransparentCanvasTexture() {
  return createTextureFromCanvas(createCanvas(8, 8));
}

function resolveEffectPlaneLayout(widthCm, heightCm, scaleX, scaleY, edgePaddingRatio) {
  const safeWidth = Math.max(widthCm, 0.001);
  const safeHeight = Math.max(heightCm, 0.001);
  const baseWidth = safeWidth * scaleX;
  const baseHeight = safeHeight * scaleY;
  const edgePadding = Math.max(baseWidth, baseHeight) * edgePaddingRatio;
  const planeWidth = Math.max(baseWidth, safeWidth + edgePadding * 2);
  const planeHeight = Math.max(baseHeight, safeHeight + edgePadding * 2);

  return {
    widthCm: planeWidth,
    heightCm: planeHeight,
    scaleX: planeWidth / safeWidth,
    scaleY: planeHeight / safeHeight
  };
}

function drawLogoAlphaMask(context, logoAsset, x, y, width, height) {
  context.save();
  context.drawImage(logoAsset.textureCanvas, x, y, width, height);
  context.globalCompositeOperation = "source-in";
  context.fillStyle = "#ffffff";
  context.fillRect(x, y, width, height);
  context.restore();
}

function createLogoWallGlowTexture(logoAsset, { widthCm, heightCm, scaleX, scaleY } = {}) {
  if (!logoAsset) {
    return createTransparentCanvasTexture();
  }

  const planeAspect = Math.max(0.2, (widthCm * scaleX) / Math.max(heightCm * scaleY, 0.001));
  const longSide = 1400;
  const canvasWidth = planeAspect >= 1 ? longSide : Math.round(longSide * planeAspect);
  const canvasHeight = planeAspect >= 1 ? Math.round(longSide / planeAspect) : longSide;
  const canvas = createCanvas(canvasWidth, canvasHeight);
  const context = canvas.getContext("2d");
  const maskCanvas = createCanvas(canvasWidth, canvasHeight);
  const maskContext = maskCanvas.getContext("2d");

  const maskWidth = canvasWidth / scaleX;
  const maskHeight = canvasHeight / scaleY;
  const maskX = (canvasWidth - maskWidth) / 2;
  const maskY = (canvasHeight - maskHeight) / 2;
  drawLogoAlphaMask(maskContext, logoAsset, maskX, maskY, maskWidth, maskHeight);

  const blurBase = Math.max(canvasWidth, canvasHeight);
  const passes = [
    { blur: blurBase * 0.072, alpha: 0.3 },
    { blur: blurBase * 0.047, alpha: 0.34 },
    { blur: blurBase * 0.026, alpha: 0.32 },
    { blur: blurBase * 0.012, alpha: 0.26 },
    { blur: blurBase * 0.004, alpha: 0.2 }
  ];

  passes.forEach((pass) => {
    context.save();
    context.globalAlpha = pass.alpha;
    context.filter = `blur(${pass.blur}px)`;
    context.drawImage(maskCanvas, 0, 0);
    context.restore();
  });

  const texture = createTextureFromCanvas(canvas);
  texture.generateMipmaps = false;
  return texture;
}

function createLogoWallShadowTexture(logoAsset, { widthCm, heightCm, scaleX, scaleY } = {}) {
  if (!logoAsset) {
    return createTransparentCanvasTexture();
  }

  const planeAspect = Math.max(0.2, (widthCm * scaleX) / Math.max(heightCm * scaleY, 0.001));
  const longSide = 1100;
  const canvasWidth = planeAspect >= 1 ? longSide : Math.round(longSide * planeAspect);
  const canvasHeight = planeAspect >= 1 ? Math.round(longSide / planeAspect) : longSide;
  const canvas = createCanvas(canvasWidth, canvasHeight);
  const context = canvas.getContext("2d");
  const outerMaskCanvas = createCanvas(canvasWidth, canvasHeight);
  const outerMaskContext = outerMaskCanvas.getContext("2d");

  const maskWidth = canvasWidth / scaleX;
  const maskHeight = canvasHeight / scaleY;
  const maskX = (canvasWidth - maskWidth) / 2;
  const maskY = (canvasHeight - maskHeight) / 2;
  const outerScale = 1.01;
  const outerWidth = maskWidth * outerScale;
  const outerHeight = maskHeight * outerScale;
  const outerX = maskX - (outerWidth - maskWidth) / 2;
  const outerY = maskY - (outerHeight - maskHeight) / 2;

  drawLogoAlphaMask(outerMaskContext, logoAsset, outerX, outerY, outerWidth, outerHeight);

  const blurBase = Math.max(canvasWidth, canvasHeight);
  const passes = [
    { source: outerMaskCanvas, blur: blurBase * 0.0034, alpha: 0.24 },
    { source: outerMaskCanvas, blur: blurBase * 0.0018, alpha: 0.34 }
  ];

  passes.forEach((pass) => {
    context.save();
    context.globalAlpha = pass.alpha;
    context.filter = `blur(${pass.blur}px)`;
    context.drawImage(pass.source, 0, 0);
    context.restore();
  });

  const texture = createTextureFromCanvas(canvas);
  texture.generateMipmaps = false;
  return texture;
}

function createSaturatedGlowColor(color) {
  const glowColor = new THREE.Color(color);
  const hsl = {};
  glowColor.getHSL(hsl);
  glowColor.setHSL(hsl.h, Math.min(1, hsl.s * 1.18 + 0.04), Math.max(0.48, hsl.l));
  return glowColor;
}

function createDemoLogoDataUrl() {
  const canvas = createCanvas(920, 360);
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.font = "900 168px Arial, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineJoin = "round";
  context.strokeStyle = "rgba(255,255,255,0.96)";
  context.lineWidth = 26;
  context.strokeText("LOGO", canvas.width / 2, canvas.height / 2 + 12);
  context.fillStyle = "#0b6f8a";
  context.fillText("LOGO", canvas.width / 2, canvas.height / 2 + 12);
  context.fillStyle = "#ff3b30";
  context.beginPath();
  context.arc(132, 92, 42, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#40d461";
  context.beginPath();
  context.arc(790, 270, 36, 0, Math.PI * 2);
  context.fill();
  return canvas.toDataURL("image/png");
}

function resolveShellStructure(totalThicknessCm = DEFAULT_TOTAL_THICKNESS_CM, glowThicknessCm = DEFAULT_GLOW_THICKNESS_CM) {
  const safeGlowThickness = clamp(glowThicknessCm, 0, 20);
  const safeTotalThickness = clamp(Math.max(totalThicknessCm, safeGlowThickness), 1, 20);
  const blackPerSideCm = Math.max((safeTotalThickness - safeGlowThickness) / 2, 0);
  const frontLipDepthCm = Math.min(FRONT_LIP_DEPTH, blackPerSideCm);

  return {
    totalThicknessCm: safeTotalThickness,
    glowThicknessCm: Math.min(safeGlowThickness, safeTotalThickness),
    blackPerSideCm,
    frontLipDepthCm,
    frontBodyBlackDepthCm: Math.max(blackPerSideCm - frontLipDepthCm, 0),
    backBlackDepthCm: blackPerSideCm,
    halfDepthCm: safeTotalThickness / 2
  };
}

class LightboxScene {
  constructor(container) {
    this.container = container;
    this.logoAsset = null;
    this.dimensions = { ...DEFAULT_PREVIEW_DIMENSIONS };
    this.mounting = "wall";
    this.lighting = "on";
    this.sideGlowColor = "#ffffff";
    this.sideGlowOpaque = false;
    this.materialRefs = {};
    this.effectTextureLogoAsset = null;
    this.effectTextureAspect = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(LIGHT_ON_BACKGROUND);
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 1000);
    this.camera.position.set(24, 14, 72);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.container.appendChild(this.renderer.domElement);
    this.annotationCanvas = document.createElement("canvas");
    this.annotationCanvas.className = "lbp-dimension-overlay";
    this.annotationContext = this.annotationCanvas.getContext("2d");
    this.annotationDpr = 1;
    this.container.appendChild(this.annotationCanvas);

    this.controls = new OrbitControlsClass(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minDistance = 22;
    this.controls.maxDistance = 170;
    this.controls.target.set(0, 0, 0);
    this.controls.addEventListener("change", () => this.render());

    this.environmentGroup = new THREE.Group();
    this.lightboxGroup = new THREE.Group();
    this.scene.add(this.environmentGroup, this.lightboxGroup);

    this.setupEnvironment();
    this.setupLights();
    this.applyPlacement();
    this.applyLighting();
    this.frameScene();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.renderer.setAnimationLoop(() => {
      this.controls.update();
    });
    this.resize();
  }

  setupLights() {
    this.ambientLight = new THREE.HemisphereLight("#f4f8ff", "#35414b", 1.35);
    this.keyLight = new THREE.DirectionalLight("#ffffff", 1.65);
    this.keyLight.position.set(0, 0, 54);
    this.keyLight.castShadow = false;
    this.keyLight.shadow.mapSize.set(2048, 2048);
    this.keyLight.shadow.camera.near = 1;
    this.keyLight.shadow.camera.far = 180;
    this.keyLight.shadow.camera.left = -80;
    this.keyLight.shadow.camera.right = 80;
    this.keyLight.shadow.camera.top = 80;
    this.keyLight.shadow.camera.bottom = -80;

    this.rimLight = new THREE.DirectionalLight("#9ed8ff", 0.7);
    this.rimLight.position.set(-40, 22, -34);

    this.frontPoint = new THREE.PointLight("#ffffff", 0.9, 105, 1.75);
    this.frontPoint.position.set(0, 2, 18);

    this.wallWash = new THREE.PointLight("#ffffff", 0.8, 150, 1.45);
    this.wallWash.position.set(0, 0, -3.4);

    this.scene.add(this.ambientLight, this.keyLight, this.rimLight, this.frontPoint, this.wallWash);
  }

  setupEnvironment() {
    this.wallMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(420, 280),
      new THREE.MeshStandardMaterial({ color: "#dfe5e7", roughness: 0.92, metalness: 0 })
    );
    this.wallMesh.receiveShadow = true;

    this.tableMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(320, 260),
      new THREE.MeshStandardMaterial({ color: "#d8dee1", roughness: 0.86, metalness: 0 })
    );
    this.tableMesh.rotation.x = -Math.PI / 2;
    this.tableMesh.receiveShadow = true;

    this.backdropMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(360, 220),
      new THREE.MeshStandardMaterial({ color: "#eef1f2", roughness: 0.94, metalness: 0 })
    );
    this.backdropMesh.position.set(0, 48, -72);
    this.backdropMesh.receiveShadow = true;

    this.wallGlowTexture = createTransparentCanvasTexture();
    this.wallGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: this.wallGlowTexture,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      })
    );
    this.wallGlow.renderOrder = 1;

    this.wallContactShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: "#000000",
        map: createTransparentCanvasTexture(),
        transparent: true,
        opacity: 0.42,
        depthWrite: false
      })
    );
    this.wallContactShadow.renderOrder = 2;

    this.deskContactShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        map: createRadialCanvasTexture({ innerAlpha: 0.52, outerAlpha: 0, width: 1024, height: 512, darkCenter: true }),
        transparent: true,
        opacity: 0.28,
        depthWrite: false
      })
    );
    this.deskContactShadow.rotation.x = -Math.PI / 2;

    this.environmentGroup.add(
      this.wallMesh,
      this.tableMesh,
      this.backdropMesh,
      this.wallGlow,
      this.wallContactShadow,
      this.deskContactShadow
    );
  }

  updateWallGlowTexture() {
    if (!this.wallGlow?.material || !this.logoAsset) {
      return;
    }

    const effectAspect = this.dimensions.widthCm / Math.max(this.dimensions.heightCm, MIN_RENDER_DIMENSION_CM);
    const sameEffectTexture =
      this.effectTextureLogoAsset === this.logoAsset &&
      Math.abs(this.effectTextureAspect - effectAspect) <= Math.max(1, effectAspect) * 1e-8;
    if (sameEffectTexture) {
      return;
    }

    const glowLayout = resolveEffectPlaneLayout(
      this.dimensions.widthCm,
      this.dimensions.heightCm,
      WALL_GLOW_SCALE_X,
      WALL_GLOW_SCALE_Y,
      WALL_GLOW_EDGE_PADDING_RATIO
    );
    const previousTexture = this.wallGlow.material.map;
    const texture = createLogoWallGlowTexture(this.logoAsset, {
      widthCm: this.dimensions.widthCm,
      heightCm: this.dimensions.heightCm,
      scaleX: glowLayout.scaleX,
      scaleY: glowLayout.scaleY
    });
    this.wallGlow.material.map = texture;
    this.wallGlow.material.needsUpdate = true;
    this.wallGlowTexture = texture;

    if (previousTexture) {
      previousTexture.dispose();
    }

    if (this.wallContactShadow?.material) {
      const shadowLayout = resolveEffectPlaneLayout(
        this.dimensions.widthCm,
        this.dimensions.heightCm,
        WALL_SHADOW_SCALE_X,
        WALL_SHADOW_SCALE_Y,
        WALL_SHADOW_EDGE_PADDING_RATIO
      );
      const previousShadowTexture = this.wallContactShadow.material.map;
      const shadowTexture = createLogoWallShadowTexture(this.logoAsset, {
        widthCm: this.dimensions.widthCm,
        heightCm: this.dimensions.heightCm,
        scaleX: shadowLayout.scaleX,
        scaleY: shadowLayout.scaleY
      });
      this.wallContactShadow.material.map = shadowTexture;
      this.wallContactShadow.material.needsUpdate = true;

      if (previousShadowTexture) {
        previousShadowTexture.dispose();
      }
    }

    this.effectTextureLogoAsset = this.logoAsset;
    this.effectTextureAspect = effectAspect;
  }

  createExtrudedMesh(shapes, depth, zStart, faceMaterial, sideMaterial, curveSegments = 22, uvGenerator = null) {
    const geometry = new THREE.ExtrudeGeometry(shapes, {
      depth,
      bevelEnabled: false,
      curveSegments,
      UVGenerator: uvGenerator || createNormalizedUvGenerator(this.dimensions.widthCm, this.dimensions.heightCm)
    });
    geometry.translate(0, 0, zStart);
    const mesh = new THREE.Mesh(geometry, [faceMaterial, sideMaterial]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  setLogoAsset(logoAsset) {
    this.logoAsset = logoAsset;
    this.rebuildSign();
  }

  setDimensions(dimensions) {
    this.dimensions = dimensions;
    this.rebuildSign();
  }

  setLogoConfiguration({ logoAsset, dimensions, mounting, lighting, sideGlowColor, sideGlowOpaque }) {
    this.logoAsset = logoAsset || this.logoAsset;
    this.dimensions = dimensions || this.dimensions;
    this.mounting = mounting || this.mounting;
    this.lighting = lighting || this.lighting;
    this.sideGlowColor = sideGlowColor || this.sideGlowColor;
    if (typeof sideGlowOpaque === "boolean") {
      this.sideGlowOpaque = sideGlowOpaque;
    }
    this.rebuildSign();
  }

  setOptions({ mounting, lighting, sideGlowColor, sideGlowOpaque }) {
    this.mounting = mounting || this.mounting;
    this.lighting = lighting || this.lighting;
    this.sideGlowColor = sideGlowColor || this.sideGlowColor;
    if (typeof sideGlowOpaque === "boolean") {
      this.sideGlowOpaque = sideGlowOpaque;
    }
    this.applyPlacement();
    this.applyLighting();
    this.frameScene();
    this.render();
  }

  rebuildSign() {
    while (this.lightboxGroup.children.length) {
      const child = this.lightboxGroup.children.pop();
      child.traverse((item) => {
        if (item.geometry) item.geometry.dispose();
        if (item.material) {
          const materials = Array.isArray(item.material) ? item.material : [item.material];
          materials.forEach((material) => {
            ["map", "emissiveMap"].forEach((key) => {
              if (material[key]) material[key].dispose();
            });
            material.dispose();
          });
        }
      });
    }

    if (!this.logoAsset) {
      return;
    }

    const widthCm = this.dimensions.widthCm;
    const heightCm = this.dimensions.heightCm;
    this.updateWallGlowTexture();

    const shell = resolveShellStructure();
    const panelInset = SHELL_WALL_THICKNESS + PANEL_FIT_GAP;
    const frontOpeningInset = panelInset + FRONT_LIP_OVERLAP;
    const structuralLoops = this.logoAsset.normalizedLoops?.length
      ? this.logoAsset.normalizedLoops
      : this.logoAsset.solidNormalizedLoops;
    const shellInnerLoops = insetLogoLoops(structuralLoops, {
      widthCm,
      heightCm,
      insetCm: SHELL_WALL_THICKNESS,
      outlineMode: this.logoAsset.outlineMode
    });
    const panelLoops = insetLogoLoops(structuralLoops, {
      widthCm,
      heightCm,
      insetCm: panelInset,
      outlineMode: this.logoAsset.outlineMode
    });
    const frontOpeningLoops = insetLogoLoops(structuralLoops, {
      widthCm,
      heightCm,
      insetCm: frontOpeningInset,
      outlineMode: this.logoAsset.outlineMode
    });

    const outerShapes = buildShapes(structuralLoops, widthCm, heightCm);
    const shellInnerShapes = buildShapes(shellInnerLoops, widthCm, heightCm);
    const panelShapes = buildShapes(panelLoops, widthCm, heightCm);
    const frontOpeningShapes = buildShapes(frontOpeningLoops, widthCm, heightCm);

    if (!outerShapes.length || !shellInnerShapes.length || !panelShapes.length || !frontOpeningShapes.length) {
      return;
    }

    const logoTexture = createTextureFromCanvas(this.logoAsset.frontTextureCanvas);
    const logoEmissionTexture = createTextureFromCanvas(this.logoAsset.emissiveTextureCanvas);
    const textureAnisotropy = this.renderer.capabilities.getMaxAnisotropy?.() || 1;
    logoTexture.anisotropy = textureAnisotropy;
    logoEmissionTexture.anisotropy = textureAnisotropy;
    const uvGenerator = createNormalizedUvGenerator(widthCm, heightCm);
    const frontLipShapes = buildRingShapes(outerShapes, frontOpeningShapes);
    const bodyRingShapes = buildRingShapes(outerShapes, shellInnerShapes);

    const frontFaceMaterial = new THREE.MeshPhysicalMaterial({
      color: "#ffffff",
      map: logoTexture,
      emissiveMap: logoEmissionTexture,
      emissive: "#ffffff",
      emissiveIntensity: 0.22,
      roughness: 0.7,
      metalness: 0.01,
      clearcoat: 0.12,
      clearcoatRoughness: 0.42,
      transparent: true,
      alphaTest: 0.03
    });

    const frontEdgeMaterial = new THREE.MeshPhysicalMaterial({
      color: "#f1f4f5",
      roughness: 0.58,
      metalness: 0.02
    });

    const shellBlackMaterial = new THREE.MeshPhysicalMaterial({
      color: SHELL_BLACK_COLOR,
      roughness: 0.86,
      metalness: 0.03
    });

    const hiddenCapMaterial = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide
    });

    const glowLayerMaterial = new THREE.MeshBasicMaterial({
      color: this.sideGlowColor,
      transparent: false,
      opacity: 1,
      toneMapped: false
    });
    const glowLayerOpaqueMaterial = shellBlackMaterial.clone();

    const backPanelMaterial = new THREE.MeshBasicMaterial({
      color: "#ffffff"
    });

    this.materialRefs = {
      frontFace: frontFaceMaterial,
      glowLayer: glowLayerMaterial,
      glowLayerColor: glowLayerMaterial,
      glowLayerOpaque: glowLayerOpaqueMaterial,
      shell: shellBlackMaterial
    };

    const halfDepth = shell.halfDepthCm;
    const frontPanelZ = halfDepth - shell.frontLipDepthCm - 0.08 - FRONT_PANEL_DEPTH;
    const backPanelZ = -halfDepth + 0.05;
    const glowLayerStartZ = -halfDepth + shell.backBlackDepthCm;
    const frontBlackStartZ = glowLayerStartZ + shell.glowThicknessCm;
    const frontLipStartZ = frontBlackStartZ + shell.frontBodyBlackDepthCm;

    const frontPanel = this.createExtrudedMesh(
      panelShapes,
      FRONT_PANEL_DEPTH,
      frontPanelZ,
      frontFaceMaterial,
      frontEdgeMaterial,
      28,
      uvGenerator
    );
    const frontLip = this.createExtrudedMesh(
      frontLipShapes,
      shell.frontLipDepthCm,
      frontLipStartZ,
      shellBlackMaterial.clone(),
      shellBlackMaterial.clone(),
      28
    );
    const frontBlack = this.createExtrudedMesh(
      bodyRingShapes,
      shell.frontBodyBlackDepthCm,
      frontBlackStartZ,
      hiddenCapMaterial,
      shellBlackMaterial.clone(),
      28
    );
    const glowLayer = this.createExtrudedMesh(
      bodyRingShapes,
      shell.glowThicknessCm,
      glowLayerStartZ,
      hiddenCapMaterial.clone(),
      glowLayerMaterial,
      28
    );
    this.materialRefs.glowLayerMesh = glowLayer;
    const backBlack = this.createExtrudedMesh(
      bodyRingShapes,
      shell.backBlackDepthCm,
      -halfDepth,
      hiddenCapMaterial.clone(),
      shellBlackMaterial.clone(),
      28
    );
    const backPanel = this.createExtrudedMesh(
      panelShapes,
      BACK_PANEL_DEPTH,
      backPanelZ,
      backPanelMaterial,
      backPanelMaterial.clone(),
      24,
      uvGenerator
    );

    this.lightboxGroup.add(backBlack, glowLayer, frontBlack, frontLip, backPanel, frontPanel);
    this.applyPlacement();
    this.applyLighting();
    this.frameScene();
    this.render();
  }

  applyLighting() {
    const isOn = this.lighting === "on";
    const baseGlowColor = new THREE.Color(this.sideGlowColor);
    const glowColor = createSaturatedGlowColor(this.sideGlowColor);
    const hasGlow = isOn && !this.sideGlowOpaque && Math.max(glowColor.r, glowColor.g, glowColor.b) > 0.02;

    if (this.materialRefs.frontFace) {
      this.materialRefs.frontFace.emissiveIntensity = isOn ? 0.56 : 0.02;
    }

    if (this.materialRefs.glowLayer) {
      if (this.materialRefs.glowLayerMesh && this.materialRefs.glowLayerColor && this.materialRefs.glowLayerOpaque) {
        this.materialRefs.glowLayerMesh.material[1] = this.sideGlowOpaque
          ? this.materialRefs.glowLayerOpaque
          : this.materialRefs.glowLayerColor;
        this.materialRefs.glowLayer = this.materialRefs.glowLayerMesh.material[1];
      }
      this.materialRefs.glowLayer.color.copy(this.sideGlowOpaque ? new THREE.Color(SHELL_BLACK_COLOR) : isOn ? glowColor : baseGlowColor);
      this.materialRefs.glowLayer.opacity = 1;
      this.materialRefs.glowLayer.transparent = false;
      this.materialRefs.glowLayer.needsUpdate = true;
    }

    if (this.mounting === "wall") {
      this.scene.background = new THREE.Color(isOn ? LIGHT_ON_BACKGROUND : LIGHT_OFF_BACKGROUND);
      if (this.wallMesh?.material) {
        this.wallMesh.material.color.set(isOn ? LIGHT_ON_BACKGROUND : LIGHT_OFF_WALL);
      }
    }

    this.wallGlow.material.color.copy(glowColor);
    this.wallGlow.material.opacity = hasGlow ? (this.mounting === "wall" ? 1 : 0.34) : 0;
    if (this.wallContactShadow?.material) {
      this.wallContactShadow.material.opacity = isOn ? 0.46 : 0.34;
    }
    this.wallWash.color.copy(glowColor);
    this.wallWash.intensity = hasGlow ? (this.mounting === "wall" ? 1.45 : 0.85) : 0.06;
    this.frontPoint.color.copy(glowColor);
    this.frontPoint.intensity = hasGlow ? 1.45 : 0.18;
    this.ambientLight.intensity = isOn ? 0.92 : 1.05;
    this.keyLight.intensity = isOn ? 1.25 : 1.95;
  }

  applyPlacement() {
    const widthCm = this.dimensions.widthCm;
    const heightCm = this.dimensions.heightCm;
    const glowLayout = resolveEffectPlaneLayout(
      widthCm,
      heightCm,
      WALL_GLOW_SCALE_X,
      WALL_GLOW_SCALE_Y,
      WALL_GLOW_EDGE_PADDING_RATIO
    );
    const shadowLayout = resolveEffectPlaneLayout(
      widthCm,
      heightCm,
      WALL_SHADOW_SCALE_X,
      WALL_SHADOW_SCALE_Y,
      WALL_SHADOW_EDGE_PADDING_RATIO
    );
    const shell = resolveShellStructure();
    const halfDepth = shell.halfDepthCm;

    if (this.mounting === "desk") {
      const tableY = -heightCm / 2 - 0.08;
      this.scene.background = new THREE.Color("#eef1f2");
      this.wallMesh.visible = false;
      this.tableMesh.visible = true;
      this.backdropMesh.visible = true;
      this.tableMesh.position.set(0, tableY, 30);
      this.backdropMesh.position.set(0, heightCm * 0.24, -70);
      this.wallGlow.visible = true;
      this.wallGlow.position.set(0, heightCm * 0.1, -55);
      this.wallGlow.scale.set(glowLayout.widthCm, glowLayout.heightCm, 1);
      this.wallContactShadow.visible = false;
      this.deskContactShadow.visible = true;
      this.deskContactShadow.position.set(0, tableY + 0.025, 0);
      this.deskContactShadow.scale.set(widthCm * 1.28, Math.max(widthCm * 0.48, 14), 1);
      this.lightboxGroup.position.set(0, 0, 0);
      this.lightboxGroup.rotation.set(0, -0.14, 0);
      this.wallWash.position.set(0, heightCm * 0.1, -28);
      this.camera.position.set(widthCm * 0.78, heightCm * 0.38, Math.max(widthCm, heightCm) * 3.25);
    } else {
      const wallZ = -halfDepth - 0.28;
      this.scene.background = new THREE.Color(this.lighting === "on" ? LIGHT_ON_BACKGROUND : LIGHT_OFF_BACKGROUND);
      this.wallMesh.visible = true;
      this.tableMesh.visible = false;
      this.backdropMesh.visible = false;
      this.wallMesh.position.set(0, 0, wallZ);
      this.wallGlow.visible = true;
      this.wallGlow.position.set(0, 0, wallZ + 0.025);
      this.wallGlow.scale.set(glowLayout.widthCm, glowLayout.heightCm, 1);
      this.wallContactShadow.visible = true;
      this.wallContactShadow.position.set(0, 0, wallZ + 0.035);
      this.wallContactShadow.scale.set(shadowLayout.widthCm, shadowLayout.heightCm, 1);
      this.deskContactShadow.visible = false;
      this.lightboxGroup.position.set(0, 0, 0);
      this.lightboxGroup.rotation.set(0, 0, 0);
      this.wallWash.position.set(0, 0, wallZ + 0.12);
      this.camera.position.set(widthCm * 1.22, heightCm * 0.3, Math.max(widthCm, heightCm) * 3.05);
    }

    this.controls.target.set(0, heightCm * 0.015, 0);
    this.controls.update();
  }

  frameScene() {
    const span = Math.max(this.dimensions.widthCm, this.dimensions.heightCm, 20);
    this.controls.minDistance = span * 1.05;
    this.controls.maxDistance = span * 5.2;
    this.camera.near = 0.1;
    this.camera.far = Math.max(700, span * 12);
    this.camera.updateProjectionMatrix();
  }

  resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2.5);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(pixelRatio, 2));
    this.renderer.setSize(width, height, false);
    this.annotationDpr = pixelRatio;
    this.annotationCanvas.width = Math.round(width * pixelRatio);
    this.annotationCanvas.height = Math.round(height * pixelRatio);
    this.annotationCanvas.style.width = `${width}px`;
    this.annotationCanvas.style.height = `${height}px`;
    this.render();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
    this.drawLiveDimensionAnnotations();
  }

  drawLiveDimensionAnnotations() {
    if (!this.annotationContext || !this.annotationCanvas) {
      return;
    }

    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    const dpr = this.annotationDpr || 1;
    this.annotationContext.clearRect(0, 0, this.annotationCanvas.width, this.annotationCanvas.height);

    if (!this.logoAsset || width <= 1 || height <= 1) {
      return;
    }

    this.annotationContext.save();
    this.annotationContext.scale(dpr, dpr);
    const annotationScale = width < 640 ? 0.48 : width < 980 ? 0.62 : 0.74;
    this.drawDimensionAnnotations(this.annotationContext, width, height, annotationScale);
    this.annotationContext.restore();
  }

  projectWorldPointToScreen(worldPoint, width, height) {
    const projected = worldPoint.clone().project(this.camera);
    return {
      x: (projected.x * 0.5 + 0.5) * width,
      y: (-projected.y * 0.5 + 0.5) * height,
      visible: projected.z >= -1 && projected.z <= 1
    };
  }

  getDimensionAnnotations(width, height) {
    const shell = resolveShellStructure();
    const widthCm = this.dimensions.widthCm;
    const heightCm = this.dimensions.heightCm;
    const halfDepth = shell.halfDepthCm;
    const cameraWorldPosition = this.camera.getWorldPosition(new THREE.Vector3());

    this.camera.updateMatrixWorld(true);
    this.lightboxGroup.updateWorldMatrix(true, false);

    const cameraLocalPosition = this.lightboxGroup.worldToLocal(cameraWorldPosition.clone());
    const faceZ = cameraLocalPosition.z >= 0 ? halfDepth : -halfDepth;
    const edgeX = cameraLocalPosition.x >= 0 ? widthCm / 2 : -widthCm / 2;
    const edgeY = cameraLocalPosition.y >= 0 ? heightCm / 2 : -heightCm / 2;
    const annotationHalfDepth = DISPLAY_THICKNESS_CM / 2;
    const depthStartZ = cameraLocalPosition.z >= 0 ? -annotationHalfDepth : annotationHalfDepth;
    const depthEndZ = cameraLocalPosition.z >= 0 ? annotationHalfDepth : -annotationHalfDepth;
    const specs = [
      {
        label: formatDimensionLabel(widthCm),
        start: new THREE.Vector3(-widthCm / 2, edgeY, faceZ),
        end: new THREE.Vector3(widthCm / 2, edgeY, faceZ)
      },
      {
        label: formatDimensionLabel(heightCm),
        start: new THREE.Vector3(edgeX, -heightCm / 2, faceZ),
        end: new THREE.Vector3(edgeX, heightCm / 2, faceZ)
      },
      {
        label: formatDimensionLabel(DISPLAY_THICKNESS_CM, { minimum: true }),
        minimumLength: 8,
        start: new THREE.Vector3(edgeX, edgeY, depthStartZ),
        end: new THREE.Vector3(edgeX, edgeY, depthEndZ)
      }
    ];

    return specs
      .map((spec) => {
        const start = this.projectWorldPointToScreen(this.lightboxGroup.localToWorld(spec.start.clone()), width, height);
        const end = this.projectWorldPointToScreen(this.lightboxGroup.localToWorld(spec.end.clone()), width, height);
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const length = Math.hypot(dx, dy);
        if ((!start.visible && !end.visible) || length < (spec.minimumLength || 16)) {
          return null;
        }

        const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
        const center = { x: width / 2, y: height / 2 };
        let normalX = -dy / Math.max(length, 1);
        let normalY = dx / Math.max(length, 1);
        if (normalX * (center.x - midpoint.x) + normalY * (center.y - midpoint.y) > 0) {
          normalX *= -1;
          normalY *= -1;
        }
        const offset = width < 640 ? 18 : width < 980 ? 26 : 34;
        const labelGap = width < 640 ? 13 : width < 980 ? 19 : 26;

        return {
          label: spec.label,
          start,
          end,
          lineStart: { x: start.x + normalX * offset, y: start.y + normalY * offset },
          lineEnd: { x: end.x + normalX * offset, y: end.y + normalY * offset },
          labelPosition: { x: midpoint.x + normalX * (offset + labelGap), y: midpoint.y + normalY * (offset + labelGap) }
        };
      })
      .filter(Boolean);
  }

  drawDimensionAnnotations(context, width, height, scale = 1) {
    const annotations = this.getDimensionAnnotations(width, height);
    if (!annotations.length) {
      return;
    }

    context.save();
    const fontSize = Math.max(10.5, 20 * scale);
    context.font = `650 ${fontSize}px Inter, "Segoe UI", sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.strokeStyle = "rgba(245, 250, 252, 0.86)";
    context.fillStyle = "rgba(13, 18, 23, 0.86)";
    context.lineWidth = Math.max(0.9, 1.35 * scale);
    context.lineCap = "round";
    context.lineJoin = "round";

    annotations.forEach((annotation) => {
      context.beginPath();
      context.moveTo(annotation.start.x, annotation.start.y);
      context.lineTo(annotation.lineStart.x, annotation.lineStart.y);
      context.moveTo(annotation.end.x, annotation.end.y);
      context.lineTo(annotation.lineEnd.x, annotation.lineEnd.y);
      context.moveTo(annotation.lineStart.x, annotation.lineStart.y);
      context.lineTo(annotation.lineEnd.x, annotation.lineEnd.y);
      context.stroke();

      const metrics = context.measureText(annotation.label);
      const pillWidth = metrics.width + Math.max(14, 28 * scale);
      const pillHeight = Math.max(19, 29 * scale);
      const safeMargin = 5;
      const labelX = clamp(annotation.labelPosition.x, pillWidth / 2 + safeMargin, width - pillWidth / 2 - safeMargin);
      const labelY = clamp(annotation.labelPosition.y, pillHeight / 2 + safeMargin, height - pillHeight / 2 - safeMargin);
      const pillX = labelX - pillWidth / 2;
      const pillY = labelY - pillHeight / 2;
      drawRoundedRect(context, pillX, pillY, pillWidth, pillHeight, pillHeight / 2);
      context.fill();
      context.fillStyle = "rgba(245, 250, 252, 0.98)";
      context.fillText(annotation.label, labelX, labelY + 0.5 * scale);
      context.fillStyle = "rgba(13, 18, 23, 0.84)";
    });

    context.restore();
  }

  renderPreviewCanvas(width, height) {
    const previousSize = this.renderer.getSize(new THREE.Vector2());
    const previousPixelRatio = this.renderer.getPixelRatio();
    const previousAspect = this.camera.aspect;

    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.render();

    const canvas = createCanvas(width, height);
    const context = canvas.getContext("2d");
    context.drawImage(this.renderer.domElement, 0, 0, width, height);
    this.drawDimensionAnnotations(context, width, height);

    this.renderer.setPixelRatio(previousPixelRatio);
    this.renderer.setSize(previousSize.x, previousSize.y, false);
    this.camera.aspect = previousAspect;
    this.camera.updateProjectionMatrix();
    this.render();

    return canvas;
  }
}

function drawRoundedRect(context, x, y, width, height, radius) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function cmToIn(valueCm) {
  return valueCm / CM_PER_INCH;
}

function formatCalculatedSize(dimensions) {
  return `${dimensions.widthCm.toFixed(1)} x ${dimensions.heightCm.toFixed(1)} cm / ${cmToIn(dimensions.widthCm).toFixed(1)} x ${cmToIn(dimensions.heightCm).toFixed(1)} in`;
}

/*
function formatDimensionLabel(valueCm, { minimum = false } = {}) {
  return `${minimum ? "≥" : ""}${valueCm.toFixed(1)} cm / ${cmToIn(valueCm).toFixed(1)}"`;
}

*/
function formatDimensionLabel(valueCm, { minimum = false } = {}) {
  const prefix = minimum ? "\u2265" : "";
  return `${prefix}${valueCm.toFixed(1)} cm / ${cmToIn(valueCm).toFixed(1)}"`;
}

function getSelectedSizeLabel() {
  if (state.sizeMode === "custom") {
    return `${state.longestSideCm.toFixed(1)}cm / ${cmToIn(state.longestSideCm).toFixed(1)}in Custom`;
  }
  return STANDARD_SIZE_OPTIONS[state.standardSizeCm];
}

function calculateDimensions() {
  const rawAspect = state.logoAsset?.aspect;
  const aspect = Number.isFinite(rawAspect) && rawAspect > 0 ? rawAspect : 1;
  const rawLongest = state.longestSideCm;
  const longest = Number.isFinite(rawLongest) && rawLongest > 0 ? rawLongest : DEFAULT_PREVIEW_DIMENSIONS.widthCm;

  if (aspect >= 1) {
    state.dimensions = {
      widthCm: Math.max(longest, MIN_RENDER_DIMENSION_CM),
      heightCm: Math.max(longest / aspect, MIN_RENDER_DIMENSION_CM)
    };
  } else {
    state.dimensions = {
      widthCm: Math.max(longest * aspect, MIN_RENDER_DIMENSION_CM),
      heightCm: Math.max(longest, MIN_RENDER_DIMENSION_CM)
    };
  }
}

function round1(value) {
  return Math.round(value * 10) / 10;
}

function setFileStatus(message, type = "") {
  if (!elements.fileStatus) {
    return;
  }
  elements.fileStatus.hidden = !message;
  elements.fileStatus.textContent = message;
  elements.fileStatus.classList.toggle("warning", type === "warning");
  elements.fileStatus.classList.toggle("error", type === "error");
}

function setRenderStatus(message) {
  elements.renderStatus.textContent = message;
}

function setQuoteStatus(message, type = "") {
  if (!elements.quoteStatus) {
    return;
  }
  elements.quoteStatus.hidden = !message;
  elements.quoteStatus.textContent = message;
  elements.quoteStatus.classList.toggle("success", type === "success");
  elements.quoteStatus.classList.toggle("error", type === "error");
}

function setQuoteSubmitting(isSubmitting) {
  if (!elements.quoteSubmit) {
    return;
  }
  elements.quoteSubmit.disabled = isSubmitting;
  elements.quoteSubmit.textContent = isSubmitting ? "Submitting..." : "Submit";
}

function setViewportMessage(message, type = "") {
  if (!elements.viewportMessage) {
    return;
  }
  elements.viewportMessage.hidden = !message;
  elements.viewportMessage.textContent = message;
  elements.viewportMessage.classList.toggle("error", type === "error");
}

function markPreviewBooted() {
  window.__lightboxPreviewBooted = true;
  setViewportMessage("");
}

function renderColorOptions() {
  elements.colorGrid.innerHTML = "";

  SIDE_GLOW_PRESETS.forEach((preset) => {
    const button = document.createElement("button");
    button.className = "lbp-color-button";
    button.type = "button";
    button.dataset.color = preset.color;
    button.dataset.slug = preset.slug;
    button.title = `${preset.name} ${preset.color}`;
    button.innerHTML = `<span class="lbp-swatch" style="--swatch-color: ${preset.color}"></span><span>${preset.name}</span>`;
    button.addEventListener("click", () => {
      state.sideGlow = preset;
      syncUi();
      scene.setOptions({ sideGlowColor: preset.color, sideGlowOpaque: Boolean(preset.opaque) });
    });
    elements.colorGrid.appendChild(button);
  });
}

function syncUi() {
  elements.customPanel.hidden = state.sizeMode !== "custom";
  if (elements.sizeReadout) {
    elements.sizeReadout.textContent = `Overall Size: ${formatCalculatedSize(state.dimensions)}`;
  }
  elements.selectedSizeMeta.textContent = `Selected Longest Side: ${getSelectedSizeLabel()}`;
  elements.colorMeta.textContent = `Side Glow Color: ${state.sideGlow.name}`;
  elements.previewTitle.textContent = `Light ${state.lighting === "on" ? "On" : "Off"}`;

  elements.sizeOptions.querySelectorAll("button").forEach((button) => {
    const isActive =
      (state.sizeMode === "custom" && button.dataset.size === "custom") ||
      (state.sizeMode === "standard" && Number(button.dataset.size) === state.standardSizeCm);
    button.classList.toggle("active", isActive);
    button.setAttribute("aria-pressed", String(isActive));
  });

  elements.unitToggle.querySelectorAll("button").forEach((button) => {
    button.classList.toggle("active", button.dataset.unit === state.customUnit);
  });

  elements.colorGrid.querySelectorAll("button").forEach((button) => {
    button.classList.toggle("active", button.dataset.slug === state.sideGlow.slug);
  });

  elements.mountingToggle?.querySelectorAll("button").forEach((button) => {
    button.classList.toggle("active", button.dataset.mounting === state.mounting);
  });

  elements.lightingToggle.querySelectorAll("button").forEach((button) => {
    button.classList.toggle("active", button.dataset.lighting === state.lighting);
  });
}

function updateDimensionsAndScene() {
  calculateDimensions();
  if (scene && state.logoAsset) {
    scene.setLogoConfiguration({
      logoAsset: state.logoAsset,
      dimensions: state.dimensions,
      mounting: state.mounting,
      lighting: state.lighting,
      sideGlowColor: state.sideGlow.color,
      sideGlowOpaque: Boolean(state.sideGlow.opaque)
    });
  }
  syncUi();
}

function validateCustomSize() {
  const value = Number(elements.customInput.value);
  const min = state.customUnit === "cm" ? 20 : 8;
  const max = state.customUnit === "cm" ? 90 : 36;
  const isValid = Number.isFinite(value) && value >= min && value <= max;
  elements.sizeValidation.hidden = isValid;
  return isValid;
}

function applyCustomSize() {
  if (!validateCustomSize()) {
    return;
  }

  state.customValue = Number(elements.customInput.value);
  state.longestSideCm = state.customUnit === "cm" ? state.customValue : state.customValue * CM_PER_INCH;
  updateDimensionsAndScene();
}

function bindEvents() {
  elements.upload.addEventListener("change", async (event) => {
    const [file] = event.target.files || [];
    if (file) {
      await handleLogoFile(file);
    }
  });

  ["dragenter", "dragover"].forEach((eventName) => {
    elements.dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      elements.dropzone.classList.add("dragging");
    });
  });

  ["dragleave", "drop"].forEach((eventName) => {
    elements.dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      elements.dropzone.classList.remove("dragging");
    });
  });

  elements.dropzone.addEventListener("drop", async (event) => {
    const [file] = event.dataTransfer.files || [];
    if (file) {
      await handleLogoFile(file);
    }
  });

  elements.sizeOptions.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-size]");
    if (!button) {
      return;
    }

    const size = button.dataset.size;
    if (size === "custom") {
      state.sizeMode = "custom";
      applyCustomSize();
      syncUi();
      return;
    }

    state.sizeMode = "standard";
    state.standardSizeCm = Number(size);
    state.longestSideCm = state.standardSizeCm;
    updateDimensionsAndScene();
  });

  elements.customInput.addEventListener("input", () => {
    if (state.sizeMode === "custom") {
      applyCustomSize();
    }
  });

  elements.unitToggle.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-unit]");
    if (!button || button.dataset.unit === state.customUnit) {
      return;
    }

    const currentValue = Number(elements.customInput.value) || state.customValue;
    if (button.dataset.unit === "inch") {
      elements.customInput.value = (currentValue / CM_PER_INCH).toFixed(1);
      elements.customInput.min = "8";
      elements.customInput.max = "36";
    } else {
      elements.customInput.value = (currentValue * CM_PER_INCH).toFixed(1);
      elements.customInput.min = "20";
      elements.customInput.max = "90";
    }

    state.customUnit = button.dataset.unit;
    applyCustomSize();
    syncUi();
  });

  elements.mountingToggle?.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-mounting]");
    if (!button) {
      return;
    }
    state.mounting = button.dataset.mounting;
    scene.setOptions({ mounting: state.mounting });
    syncUi();
  });

  elements.lightingToggle.addEventListener("click", (event) => {
    const button = event.target.closest("button[data-lighting]");
    if (!button) {
      return;
    }
    state.lighting = button.dataset.lighting;
    scene.setOptions({ lighting: state.lighting });
    syncUi();
  });

  elements.quoteForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    submitQuoteRequest().catch((error) => {
      console.error(error);
      setRenderStatus("Submit failed");
      setQuoteStatus(error.message || "Quote submission failed. Please try again.", "error");
    });
  });
}

async function handleLogoFile(file) {
  if (file.size > MAX_FILE_SIZE) {
    setFileStatus("Please upload a file smaller than 5MB.", "error");
    return;
  }

  if (!/^image\/(png|jpeg|svg\+xml)$/.test(file.type) && !/\.(png|jpe?g|svg)$/i.test(file.name)) {
    setFileStatus("Please upload PNG, JPG, or SVG.", "error");
    return;
  }

  setRenderStatus("Processing logo");
  if (elements.quoteSubmit) {
    elements.quoteSubmit.disabled = true;
  }

  try {
    const asset = await buildLogoAssetFromSource(file);
    state.logoAsset = asset;
    state.originalLogoFile = file;
    state.sourceWarning =
      asset.outlineMode === "full-image"
        ? "For JPG or non-transparent files, size calculation may be approximate. Transparent PNG or SVG is recommended."
        : "";
    calculateDimensions();
    scene.setLogoConfiguration({
      logoAsset: asset,
      dimensions: state.dimensions,
      mounting: state.mounting,
      lighting: state.lighting,
      sideGlowColor: state.sideGlow.color,
      sideGlowOpaque: Boolean(state.sideGlow.opaque)
    });
    setViewportMessage("");
    setFileStatus(state.sourceWarning || `${file.name} loaded. Visible logo area detected.`);
    setQuoteStatus("");
    if (state.sourceWarning && elements.fileStatus) {
      elements.fileStatus.classList.add("warning");
    }
    setRenderStatus("Ready");
  } catch (error) {
    console.error(error);
    setFileStatus(error.message || "Logo processing failed.", "error");
    setRenderStatus("Logo error");
  } finally {
    if (elements.quoteSubmit) {
      elements.quoteSubmit.disabled = false;
    }
    syncUi();
  }
}

function drawParameterCard(context, { x, y, width, height, generatedAt }) {
  context.save();
  context.fillStyle = "#f8faf9";
  context.fillRect(x, y, width, height);
  context.strokeStyle = "#d7e0e3";
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(x, y);
  context.lineTo(x + width, y);
  context.stroke();

  const paddingX = 64;
  const titleY = y + 54;
  context.fillStyle = "#141b22";
  context.font = '800 30px Inter, "Segoe UI", sans-serif';
  context.fillText("Custom Lightbox Preview", x + paddingX, titleY);

  const lines = [
    `Selected Longest Side: ${getSelectedSizeLabel()}`,
    `Calculated Size: ${formatCalculatedSize(state.dimensions)}`,
    `Side Glow Color: ${state.sideGlow.name}`,
    `Lighting: ${state.lighting === "on" ? "ON" : "OFF"}`,
    `Generated: ${generatedAt.display}`,
    `Generated by ${CONFIG.brandName}`
  ];

  context.font = '500 22px Inter, "Segoe UI", sans-serif';
  context.fillStyle = "#36444d";
  const columnGap = 70;
  const columnWidth = (width - paddingX * 2 - columnGap) / 2;
  lines.forEach((line, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const lineX = x + paddingX + column * (columnWidth + columnGap);
    const lineY = titleY + 48 + row * 36;
    context.fillText(line, lineX, lineY, columnWidth);
  });

  context.restore();
}

function createTimestamp() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  const yyyy = now.getFullYear();
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const mi = pad(now.getMinutes());
  const ss = pad(now.getSeconds());
  return {
    file: `${yyyy}${mm}${dd}-${hh}${mi}${ss}`,
    display: `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
  };
}

function buildFileName(timestamp, extension = "png") {
  const selected =
    state.sizeMode === "custom"
      ? `${state.longestSideCm.toFixed(1).replace(".", "-")}cm`
      : `${state.standardSizeCm}cm-${Math.round(cmToIn(state.standardSizeCm))}in`;
  const shortId = Math.random().toString(36).slice(2, 6);
  const cleanExtension = extension === "jpg" || extension === "jpeg" ? "jpg" : "png";
  return `myneonup-preview-${selected}-${state.sideGlow.slug}-${timestamp.file}-${shortId}.${cleanExtension}`;
}

function canvasToBlob(canvas, type = "image/png", quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("The preview image could not be generated."));
      }
    }, type, quality);
  });
}

function getQuotePreviewType() {
  return CONFIG.quotePreviewType === "image/png" ? "image/png" : "image/jpeg";
}

function getQuotePreviewAttemptTypes() {
  const preferredType = getQuotePreviewType();
  return preferredType === "image/png" ? ["image/png"] : [preferredType, "image/png"];
}

function getPreviewExtension(type) {
  return type === "image/png" ? "png" : "jpg";
}

function canRetryWithPng(result, previewType) {
  return (
    previewType !== "image/png" &&
    (result?.code === "preview_missing" || /preview image is missing or invalid/i.test(result?.message || ""))
  );
}

function shouldRetryWithPreviewDataUrl(result, usedPreviewDataUrl) {
  return (
    !usedPreviewDataUrl &&
    (result?.retryMode === "preview-data-url" ||
      result?.code === "preview_missing" ||
      /preview image is missing or invalid/i.test(result?.message || ""))
  );
}

function safeCanvasToDataUrl(canvas, type, quality) {
  try {
    return canvas.toDataURL(type, quality);
  } catch (error) {
    console.warn("Preview data URL fallback failed", error);
    return "";
  }
}

function createSubmissionId(timestamp) {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `lbq-${timestamp.file}-${randomPart}`;
}

function getQuoteFormValues() {
  return {
    name: elements.quoteName?.value.trim() || "",
    email: elements.quoteEmail?.value.trim() || "",
    message: elements.quoteMessage?.value.trim() || ""
  };
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateQuoteRequest() {
  const values = getQuoteFormValues();

  if (!state.originalLogoFile) {
    return "Please upload your logo before submitting.";
  }

  if (!values.name) {
    elements.quoteName?.focus();
    return "Please enter your name.";
  }

  if (!values.email || !isValidEmail(values.email)) {
    elements.quoteEmail?.focus();
    return "Please enter a valid email address.";
  }

  if (elements.quoteCompany?.value) {
    return "Please refresh the page and try again.";
  }

  if (!CONFIG.quoteSubmitUrl) {
    return "Quote submission is not configured yet. Please contact us directly.";
  }

  return "";
}

function buildSubmissionPayload(generatedAt, submissionId) {
  const form = getQuoteFormValues();
  return {
    submissionId,
    version: LIGHTBOX_PREVIEW_VERSION,
    generatedAt: generatedAt.display,
    customer: form,
    configuration: {
      selectedLongestSide: getSelectedSizeLabel(),
      longestSideCm: round1(state.longestSideCm),
      calculatedSize: formatCalculatedSize(state.dimensions),
      dimensionsCm: state.dimensions,
      dimensionsIn: {
        widthIn: round1(cmToIn(state.dimensions.widthCm)),
        heightIn: round1(cmToIn(state.dimensions.heightCm))
      },
      sideGlowColor: {
        name: state.sideGlow.name,
        slug: state.sideGlow.slug,
        value: state.sideGlow.color,
        opaque: Boolean(state.sideGlow.opaque)
      },
      lighting: state.lighting,
      mounting: state.mounting,
      thickness: formatDimensionLabel(DISPLAY_THICKNESS_CM, { minimum: true })
    },
    logo: {
      name: state.originalLogoFile?.name || state.logoAsset?.name || "uploaded-logo",
      type: state.originalLogoFile?.type || "",
      size: state.originalLogoFile?.size || 0,
      sourceType: state.logoAsset?.sourceType || "",
      outlineMode: state.logoAsset?.outlineMode || "",
      sourceSize: state.logoAsset?.sourceSize || null
    },
    page: {
      url: window.location.href,
      userAgent: navigator.userAgent
    }
  };
}

function buildQuoteRequestForm(payload, timestamp, previewBlob, previewType, options = {}) {
  const previewDataUrl = options.previewDataUrl || "";
  const form = new FormData();

  form.append("name", payload.customer.name);
  form.append("email", payload.customer.email);
  form.append("message", payload.customer.message);
  form.append("company", elements.quoteCompany?.value || "");
  form.append("configJson", JSON.stringify(payload));
  form.append("originalLogo", state.originalLogoFile, state.originalLogoFile.name || "original-logo");
  form.append("previewPng", previewBlob, buildFileName(timestamp, getPreviewExtension(previewType)));

  if (previewDataUrl) {
    form.append("previewDataUrl", previewDataUrl);
  }

  return form;
}

async function postQuoteRequest(form) {
  const response = await fetch(CONFIG.quoteSubmitUrl, {
    method: "POST",
    body: form
  });
  const contentType = response.headers.get("Content-Type") || "";
  const expectsJson = contentType.toLowerCase().includes("application/json");
  let result = null;

  if (expectsJson) {
    result = await response.json().catch(() => null);
  }

  return { response, result };
}

function trackSuccessfulQuoteSubmission(payload) {
  const trackingPayload = {
    form_type: "lightbox_quote",
    lightbox_version: LIGHTBOX_PREVIEW_VERSION,
    side_glow_color: payload.configuration.sideGlowColor.slug,
    lighting: payload.configuration.lighting,
    size_mode: state.sizeMode,
    longest_side_cm: payload.configuration.longestSideCm,
    calculated_width_cm: payload.configuration.dimensionsCm.widthCm,
    calculated_height_cm: payload.configuration.dimensionsCm.heightCm
  };

  if (CONFIG.googleAdsEventName) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: CONFIG.googleAdsEventName,
      ...trackingPayload
    });
  }

  if (CONFIG.googleAdsSendTo && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: CONFIG.googleAdsSendTo
    });
  }

  window.dispatchEvent(
    new CustomEvent("lightbox:quote-submit-success", {
      detail: trackingPayload
    })
  );
}

async function submitQuoteRequest() {
  if (!state.logoAsset) {
    throw new Error("Please upload a logo before submitting.");
  }

  const validationError = validateQuoteRequest();
  if (validationError) {
    setQuoteStatus(validationError, "error");
    return;
  }

  setQuoteStatus("");
  setRenderStatus("Submitting quote");
  setQuoteSubmitting(true);

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const previewWidth = CONFIG.quotePreviewSize?.width || 900;
    const previewHeight = CONFIG.quotePreviewSize?.height || 1125;
    const previewCanvas = scene.renderPreviewCanvas(previewWidth, previewHeight);
    const timestamp = createTimestamp();
    const submissionId = createSubmissionId(timestamp);
    const payload = buildSubmissionPayload(timestamp, submissionId);
    const previewTypes = getQuotePreviewAttemptTypes();

    let completed = false;
    let lastErrorMessage = "Quote submission failed. Please try again.";
    let lastResult = null;

    for (const previewType of previewTypes) {
      const previewBlob = await canvasToBlob(previewCanvas, previewType, CONFIG.quotePreviewQuality ?? 0.86);
      let usedPreviewDataUrl = false;

      for (;;) {
        const form = buildQuoteRequestForm(payload, timestamp, previewBlob, previewType, {
          previewDataUrl: usedPreviewDataUrl
            ? safeCanvasToDataUrl(previewCanvas, previewType, CONFIG.quotePreviewQuality ?? 0.86)
            : ""
        });
        const { response, result } = await postQuoteRequest(form);

        if (response.ok && result?.ok === true) {
          lastErrorMessage = result.message || "";
          lastResult = result;
          completed = true;
          break;
        }

        lastResult = result;
        lastErrorMessage =
          result?.message ||
          (response.ok
            ? "Quote submission returned an invalid response. Please try again."
            : "Quote submission failed. Please try again.");
        if (shouldRetryWithPreviewDataUrl(result, usedPreviewDataUrl)) {
          usedPreviewDataUrl = true;
          continue;
        }
        break;
      }

      if (completed) {
        break;
      }

      if (!canRetryWithPng(lastResult, previewType)) {
        throw new Error(lastErrorMessage);
      }
    }

    if (!completed) {
      throw new Error(lastErrorMessage);
    }

    try {
      trackSuccessfulQuoteSubmission(payload);
    } catch (error) {
      console.warn("Quote success tracking failed", error);
    }
    const successMessage =
      lastErrorMessage && /^quote request sent\./i.test(lastErrorMessage)
        ? lastErrorMessage
        : "Quote request sent. We'll get back to you within 24 hours.";
    setQuoteStatus(successMessage, "success");
    setRenderStatus("Ready");
  } catch (error) {
    throw error;
  } finally {
    setQuoteSubmitting(false);
  }
}

async function loadDemoLogo() {
  const demoSources = [
    { source: CONFIG.defaultLogoUrl, name: "Demo_logo.png" },
    { source: CONFIG.defaultLogoFallbackUrl, name: "Demo_logo.png" },
    { source: createDemoLogoDataUrl(), name: "demo-logo-fallback.png" }
  ].filter((item) => Boolean(item.source));
  let demoAsset = null;
  let lastError = null;

  for (const item of demoSources) {
    try {
      demoAsset = await buildLogoAssetFromSource(item.source, { name: item.name });
      break;
    } catch (error) {
      lastError = error;
    }
  }

  if (!demoAsset) {
    throw lastError || new Error("The demo logo could not be loaded.");
  }

  state.logoAsset = demoAsset;
  calculateDimensions();
  scene.setLogoConfiguration({
    logoAsset: demoAsset,
    dimensions: state.dimensions,
    mounting: state.mounting,
    lighting: state.lighting,
    sideGlowColor: state.sideGlow.color,
    sideGlowOpaque: Boolean(state.sideGlow.opaque)
  });
  setViewportMessage("");
  setFileStatus(`${demoAsset.name} loaded. Upload your logo to replace it.`);
  syncUi();
}

async function init() {
  await loadThreeRuntime();
  renderColorOptions();
  scene = new LightboxScene(elements.viewport);
  bindEvents();
  syncUi();
  markPreviewBooted();
  await loadDemoLogo();
  markPreviewBooted();
}

init().catch((error) => {
  console.error(error);
  setFileStatus(error.message || "The preview could not start.", "error");
  setRenderStatus("Startup error");
  setViewportMessage("The 3D preview could not start. Please check that the 3D engine files are uploaded correctly.", "error");
});
